import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Slider
} from '@bliks-io/ui';
import { useTranslation } from 'react-i18next';

interface ImageCropperProps {
  /** The image source to crop (data URL) */
  imageSrc: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Callback when the crop is confirmed with the cropped area */
  onCropComplete: (croppedArea: Area) => void;
  /** Whether the cropping is in progress */
  isLoading?: boolean;
}

/**
 * ImageCropper Component
 *
 * A dialog component for cropping images before upload.
 * Uses react-easy-crop for the cropping functionality.
 *
 * @example
 * ```tsx
 * const [imageSrc, setImageSrc] = useState<string | null>(null);
 * const [showCropper, setShowCropper] = useState(false);
 *
 * const handleCropComplete = async (croppedArea: Area) => {
 *   const croppedImage = await getCroppedImg(imageSrc, croppedArea);
 *   // Upload the cropped image
 * };
 *
 * return (
 *   <ImageCropper
 *     imageSrc={imageSrc}
 *     open={showCropper}
 *     onClose={() => setShowCropper(false)}
 *     onCropComplete={handleCropComplete}
 *   />
 * );
 * ```
 */
export function ImageCropper({
  imageSrc,
  open,
  onClose,
  onCropComplete,
  isLoading = false
}: ImageCropperProps) {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropAreaChange = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = useCallback(() => {
    if (croppedAreaPixels) {
      onCropComplete(croppedAreaPixels);
    }
  }, [croppedAreaPixels, onCropComplete]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('avatar.cropImage')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Crop Area */}
          <div className="relative h-[400px] bg-muted rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1} // Square crop for avatar
              cropShape="rect" // Square crop area
              showGrid={false}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropAreaChange}
              classes={{
                cropAreaClassName: 'rounded-lg'
              }}
              style={{
                cropAreaStyle: {
                  borderRadius: '0.5rem'
                }
              }}
            />
          </div>

          {/* Zoom Slider */}
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('avatar.zoom')}</label>
            <Slider
              value={[zoom]}
              onValueChange={([value]) => setZoom(value)}
              min={1}
              max={3}
              step={0.1}
              className="w-full"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading || !croppedAreaPixels}>
            {isLoading ? t('common.processing') : t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
